import { Avatar, Box, Typography, Chip, Button, Card, CardContent, CircularProgress } from "@mui/material";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { PipelinePickup } from "./types";
import { getRegionChipStyle, formatRequestedDate } from "./pipelineUtils";
import { allowsManualStageChange, NEXT_STAGE, PIPELINE_COLUMNS } from "./pipelineColumns";
import { NewOrderCardContent } from "./cards/NewOrderCardContent";
import { PendingConfirmationCardContent } from "./cards/PendingConfirmationCardContent";
import { ScheduledCardContent } from "./cards/ScheduledCardContent";
import { PickedUpCardContent } from "./cards/PickedUpCardContent";
import { DeliveryVerifiedCardContent } from "./cards/DeliveryVerifiedCardContent";
import NeedsActionBadge from "../NeedsActionBadge";

type Props = {
  pickup: PipelinePickup;
  onAction: (pickup: PipelinePickup) => void;
  advancing?: boolean;
};

function getActionLabel(pickup: PipelinePickup): string | null {
  if (!allowsManualStageChange(pickup.stage)) {
    return null;
  }

  const nextStage = NEXT_STAGE[pickup.stage];
  if (!nextStage) {
    return null;
  }

  const column = PIPELINE_COLUMNS.find((item) => item.stage === nextStage);
  return column ? `Advance to: ${column.title}` : null;
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function MetaRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ color: "text.disabled", display: "flex", alignItems: "center", width: 18 }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.4 }}>
        {children}
      </Typography>
    </Box>
  );
}

export default function PipelineCard({ pickup, onAction, advancing = false }: Props) {
  const actionLabel = getActionLabel(pickup);
  const regionStyle = getRegionChipStyle(pickup.region);
  const formattedDate = formatRequestedDate(pickup.requestedDate);

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        borderRadius: "10px",
        borderColor: "#E8E8E8",
        boxShadow: "none",
        backgroundColor: "background.paper",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
          <Typography
            component={RouterLink}
            to={`/logistics_platform/pickups/${pickup.uuid}`}
            variant="body2"
            color="primary"
            fontWeight={600}
            sx={{ fontSize: 13, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            {pickup.displayId}
          </Typography>
          <Chip
            label={pickup.region}
            size="small"
            sx={{
              height: 22,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.4,
              borderRadius: "4px",
              backgroundColor: regionStyle.backgroundColor,
              color: regionStyle.color,
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ fontSize: 15, lineHeight: 1.3, minWidth: 0 }}>
            {pickup.companyName}
          </Typography>
        </Box>

        {pickup.needsAction && (
          <Box sx={{ flexShrink: 0, display: "flex", mb: 1 }}>
            <NeedsActionBadge canceled={!!pickup.cancellationReason} />
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 0.5 }}>
          <MetaRow icon={<LocationOnOutlinedIcon sx={{ fontSize: 16 }} />}>
            {pickup.city}, {pickup.state}
          </MetaRow>
          <MetaRow icon={<Inventory2OutlinedIcon sx={{ fontSize: 16 }} />}>{pickup.assetCount} assets</MetaRow>
          <MetaRow icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}>
            Requested{" "}
            <Typography component="span" variant="body2" fontWeight={700} color="text.primary" sx={{ fontSize: 13 }}>
              {formattedDate}
            </Typography>
          </MetaRow>
        </Box>

        {pickup.autoRecommendation && pickup.stage === "new_orders" && (
          <NewOrderCardContent recommendation={pickup.autoRecommendation} />
        )}
        {pickup.pendingVendor && pickup.stage === "pending_confirmation" && (
          <PendingConfirmationCardContent pendingVendor={pickup.pendingVendor} />
        )}
        {pickup.confirmedVendor && pickup.stage === "scheduled_confirmed" && (
          <ScheduledCardContent confirmedVendor={pickup.confirmedVendor} />
        )}
        {pickup.pickedUpAt && pickup.stage === "picked_up" && <PickedUpCardContent pickup={pickup} />}
        {pickup.deliveredAt && pickup.stage === "delivery_verified" && <DeliveryVerifiedCardContent pickup={pickup} />}

        {actionLabel && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="small"
            disabled={advancing}
            onClick={() => onAction(pickup)}
            startIcon={advancing ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              mt: 1.5,
              py: 0.875,
              textTransform: "uppercase",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.5,
              borderRadius: "6px",
              boxShadow: "none",
            }}
          >
            {actionLabel}
          </Button>
        )}

        {pickup.coordinatorName && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1.5,
              mx: -2,
              px: 2,
              pt: 1.5,
              borderTop: "1px solid",
              borderColor: "#E8E8E8",
            }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: "primary.main",
              }}
            >
              {getInitials(pickup.coordinatorName)}
            </Avatar>
            <Typography variant="body2" sx={{ fontSize: 13, color: "#475569" }}>
              {pickup.coordinatorName}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
